import { WhiteBox } from "@foodpilot/foods";
import { Box, Typography, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import QuestionnaireIcon from "src/assets/icons/questionnaire-huge.svg?react";
import { ApiViolationHint } from "src/utils/apiViolations";

type Props = {
    /**
     * Optional structured hint emitted by the backend `SsqPostCreateProvider`.
     * When present, we surface the supplier and reference names to make the
     * "this QSS was shadowed because…" explanation concrete instead of generic.
     */
    hint?: ApiViolationHint | null;
};

/**
 * Rendered in place of the QSS form when the backend refuses to create a
 * `SsqPost` because the `(ingredient, supplier, campaign)` triplet is shadowed
 * by an `IngredientReference` carrying scores at the supplier level
 * (cf. `SsqPostCreateProvider::ERROR_INGREDIENT_SUPPLIER_HAS_RESOLVABLE_REFERENCE`,
 * plan §8 — fallback-when-override-has-no-scores: SSQ never shown when scores
 * exist for the active campaign).
 *
 * This is an *informational* state, not an error: the user did nothing wrong,
 * the platform simply has nothing to ask. We avoid red/error colours on purpose.
 */
export const SsqShadowedBySupplierReference = ({ hint }: Props) => {
    const { t } = useTranslation();

    const supplierName = hint?.supplierName ?? "";
    const referenceName = hint?.referenceName ?? "";

    return (
        <WhiteBox
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
            }}
        >
            <Box
                sx={{
                    marginBlock: "80px",
                    maxWidth: "640px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "20px",
                }}
            >
                <QuestionnaireIcon />
                <Stack gap={1.5} alignItems="center">
                    <Typography variant="h3" sx={{ textAlign: "center" }}>
                        {t(
                            "ingredient.questionnaire.shadowed_by_supplier_reference.title",
                            "No questionnaire to fill for this supplier",
                        )}
                    </Typography>
                    <Typography variant="body-medium" sx={{ textAlign: "center" }}>
                        {supplierName && referenceName ?
                            t(
                                "ingredient.questionnaire.shadowed_by_supplier_reference.body_with_names",
                                'Scores for "{{supplierName}}" are provided by the ingredient reference "{{referenceName}}". The questionnaire is not needed for this supplier on the active campaign.',
                                { supplierName, referenceName },
                            )
                        :   t(
                                "ingredient.questionnaire.shadowed_by_supplier_reference.body",
                                "Scores for this supplier are provided by an assigned ingredient reference. The questionnaire is not needed on the active campaign.",
                            )
                        }
                    </Typography>
                </Stack>
            </Box>
        </WhiteBox>
    );
};
